import { useEffect, useState } from 'react';
import axios from '../interceptors/axios';

export interface TimeSettings {
  timezone: string;
  dst_mode: 'auto' | 'off';
}

export const DEFAULT_TIME_SETTINGS: TimeSettings = { timezone: 'Europe/Kyiv', dst_mode: 'auto' };

/**
 * Account timezone, shared across the portal.
 *
 * Schedules are written in this zone, so the clock in the topbar and the
 * schedule editor must agree with it — showing the operator's browser time
 * instead would silently disagree with what the screens actually do.
 *
 * Fetched once and cached; publish() lets the settings page refresh every
 * subscriber the moment the zone changes.
 */
let cache: TimeSettings | null = null;
let inflight: Promise<TimeSettings> | null = null;
const listeners = new Set<(s: TimeSettings) => void>();

export function publishTimeSettings(next: TimeSettings) {
  cache = next;
  listeners.forEach(l => l(next));
}

function load(): Promise<TimeSettings> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = axios.get<TimeSettings>('/company/time-settings')
      .then(({ data }) => { cache = data; return data; })
      .catch(() => DEFAULT_TIME_SETTINGS)
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function useTimeSettings(): TimeSettings {
  const [settings, setSettings] = useState<TimeSettings>(cache ?? DEFAULT_TIME_SETTINGS);

  useEffect(() => {
    let alive = true;
    load().then(s => { if (alive) setSettings(s); });

    listeners.add(setSettings);
    return () => { alive = false; listeners.delete(setSettings); };
  }, []);

  return settings;
}
